// Tour Context for PFMEA Application
// Manages product tour state and navigation
// ============================================================

import React, { createContext, useContext, useState, useEffect } from 'react';

interface TourStep {
    target: string; // CSS selector
    title: string;
    content: string;
    placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
    page?: string; // Which page this step is for
}

interface TourContextType {
    isTourActive: boolean;
    currentStep: number;
    startTour: () => void;
    stopTour: () => void;
    nextStep: () => void;
    previousStep: () => void;
    tourSteps: TourStep[];
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export const useTour = () => {
    const context = useContext(TourContext);
    if (!context) {
        throw new Error('useTour must be used within TourProvider');
    }
    return context;
};

// Define tour steps for PFMEA application - AI-Centric with AIAG-VDA 7-Step workflow
const TOUR_STEPS: TourStep[] = [
    {
        target: 'body',
        title: 'Welcome to Magnus PFMEA Portal 🚀',
        content: 'Your AI-powered solution for AIAG-VDA Process FMEA. Create compliant PFMEAs faster with intelligent assistants that guide you through every step to certification.',
        placement: 'center',
    },
    // ═══════════════════════════════════════════════════════════════
    // AI TOOLS - HIGHLIGHT FIRST
    // ═══════════════════════════════════════════════════════════════
    {
        target: '[data-tour-id="ai-assistant"]',
        title: '🤖 AI Tool #1: Magnus AI Chat',
        content: 'Your intelligent PFMEA copilot! Ask questions about AIAG-VDA requirements, get help identifying failure modes, or request suggestions for prevention controls. Works like ChatGPT but specialized for automotive quality.',
        placement: 'right',
    },
    {
        target: '[data-tour-id="ai-ideation"]',
        title: '⚡ AI Tool #2: PFMEA Generator',
        content: 'The fastest way to create a complete PFMEA! Describe your manufacturing process in plain English and AI generates: Process Flow → Failure Modes → Effects → Causes → S/O/D Ratings → Controls. Solves the "blank page" problem.',
        placement: 'right',
    },
    {
        target: '[data-tour-id="magnus-assistant"]',
        title: '💬 Magnus AI - Always Available',
        content: 'See the floating button in the corner? Magnus is always here! Ask him anything: "What is Action Priority?", "How do I rate Detection?", "Show me high-risk items". Use Basic mode for quick answers or Prime mode for deep analysis.',
        placement: 'left',
    },
    // ═══════════════════════════════════════════════════════════════
    // OVERVIEW
    // ═══════════════════════════════════════════════════════════════
    {
        target: '[data-tour-id="dashboard"]',
        title: '📊 Dashboard - Your Command Center',
        content: 'Monitor your PFMEA health at a glance. The Data Quality Score shows what\'s complete vs what\'s missing. AI tools are at the top to help you close gaps quickly.',
        placement: 'right',
    },
    {
        target: '[data-tour-id="user-guide"]',
        title: '📚 User Guide - Full Documentation',
        content: 'Need detailed help? Click Guide for comprehensive documentation on AIAG-VDA methodology, AI assistants, and step-by-step tutorials.',
        placement: 'bottom',
    },
    // ═══════════════════════════════════════════════════════════════
    // AIAG-VDA 7-STEP WORKFLOW
    // ═══════════════════════════════════════════════════════════════
    {
        target: '[data-tour-id="planning"]',
        title: '📋 Step 1: Planning & Preparation',
        content: 'Start here! Define your project scope using the 5T method (InTent, Timing, Team, Tasks, Tools). Link to DFMEA and customer requirements. AI Tip: Ask Magnus "What should I include in my 5T scope?"',
        placement: 'right',
    },
    {
        target: '[data-tour-id="structure"]',
        title: '🔀 Step 2: Structure Analysis',
        content: 'Create your Process Flow Diagram. AI Tip: Use the AI PFMEA Generator to auto-create process steps from your process description!',
        placement: 'right',
    },
    {
        target: '[data-tour-id="functions"]',
        title: '⚙️ Step 3: Function Analysis',
        content: 'Define what each process step should accomplish. AI Tip: Ask Magnus "What are typical functions for a welding operation?"',
        placement: 'right',
    },
    {
        target: '[data-tour-id="failure"]',
        title: '⚠️ Step 4: Failure Analysis',
        content: 'The heart of PFMEA - identify failure modes, effects, and causes. AI Tip: The AI Generator creates failure modes automatically using industry failure databases!',
        placement: 'right',
    },
    {
        target: '[data-tour-id="risk"]',
        title: '📊 Step 5: Risk Analysis',
        content: 'Rate Severity, Occurrence, Detection to calculate Action Priority. AI Tip: AI suggests ratings based on similar failures and industry benchmarks.',
        placement: 'right',
    },
    {
        target: '[data-tour-id="optimize"]',
        title: '✅ Step 6: Optimization',
        content: 'Track actions to reduce risk. AI Tip: Ask Magnus "What actions reduce occurrence for this failure?" for prevention control ideas.',
        placement: 'right',
    },
    {
        target: '[data-tour-id="document"]',
        title: '📄 Step 7: Results Documentation',
        content: 'Generate Control Plans and Work Instructions. AI Tip: Control Plan AI auto-creates inspection requirements from your PFMEA controls!',
        placement: 'right',
    },
    {
        target: 'body',
        title: 'You\'re Ready for AI-Powered PFMEA! 🎉',
        content: 'Quick start options: 1) Use AI PFMEA Generator to create a complete PFMEA in minutes, 2) Chat with Magnus for guidance, 3) Check the Dashboard for your compliance gaps. Let\'s build something audit-ready!',
        placement: 'center',
    },
];

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isTourActive, setIsTourActive] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);

    useEffect(() => {
        // Check if user has seen tour before
        const tourSeen = localStorage.getItem('pfmea-tour-completed');
        if (!tourSeen) {
            // Could auto-start tour for new users
        }
    }, []);

    const startTour = () => {
        setIsTourActive(true);
        setCurrentStep(0);
    };

    const stopTour = () => {
        setIsTourActive(false);
        setCurrentStep(0);
        localStorage.setItem('pfmea-tour-completed', 'true');
    };

    const nextStep = () => {
        if (currentStep < TOUR_STEPS.length - 1) {
            setCurrentStep(prev => prev + 1);
        } else {
            stopTour();
        }
    };

    const previousStep = () => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        }
    };

    return (
        <TourContext.Provider
            value={{
                isTourActive,
                currentStep,
                startTour,
                stopTour,
                nextStep,
                previousStep,
                tourSteps: TOUR_STEPS,
            }}
        >
            {children}
        </TourContext.Provider>
    );
};
